import { Link } from 'react-router-dom';
import { CaretRight, Warning } from '@phosphor-icons/react';

import { HelpTooltip } from '../common/HelpTooltip';
import { HeroProgressBar } from '../common/HeroProgressBar';
import { CycleHeroShell } from '../common/CycleHeroShell';
import pt from '../../locales/pt';
import { cycleDaysRemaining } from '../../utils/cycleDays';
import { formatSelfiePeriodLabel } from '../../utils/selfiePeriod';
import {
  cycleSavingsRatePct,
  cycleSavingsReservedEur,
  cycleSpendableRemainingEur,
  cycleTotalSpendableEur,
} from '../../utils/cycleFinancialMetrics';
import { formatEUR } from '../../utils/money';
import { paths } from '../../routes/paths';

/**
 * `.pen` → `ds/card/cycle-spend-hero` (`la639`) e `ds/card/cycle-spend-hero--tight`
 * (`Mnq4E`). Ambos são **um** cartão em gradiente sage com, por esta ordem e
 * `gap $space-5`:
 *
 * 1. `cycle-nav` (setas + chip, tom `$on-hero`)
 * 2. `head` (`gap $space-2`): eyebrow + ícone · readout `$type-display-hero` ·
 *    subtítulo do orçamento
 * 3. `bar-block` (`gap $space-2`): barra 6px · escala (% disponível / dias) e,
 *    na variante `--tight`, `note-row` com a pílula `$warning-500`
 * 4. `foot` (traço superior `$on-hero-track`, `pt $space-4`): duas colunas
 *    (reservado / taxa) e `link-row` com «Planear orçamento»
 *
 * A variante `--tight` mantém o gradiente e o texto `$on-hero` — só acrescenta
 * o contorno `$warning-500`, troca o ícone do eyebrow por `warning` e a barra
 * passa a `$warning-500` a 100%.
 *
 * (Alertas de categoria vivem na secção própria `AlertsPanel`.)
 */
export function ExpensesCycleHero({
  activeCycle,
  cycles = [],
  cycleIndex = 0,
  onCycleIndexChange,
  overview,
  monthlySummary,
  isLoading = false,
  isError = false,
  onRetry,
  className,
}) {
  const remainingRaw = cycleSpendableRemainingEur(overview);
  const hasRemaining = remainingRaw !== null && overview?.has_income;
  const remaining = hasRemaining ? remainingRaw : null;
  const savingsPct = cycleSavingsRatePct(monthlySummary);
  const savingsReserved = cycleSavingsReservedEur(overview);

  const totalSpendable = cycleTotalSpendableEur(overview);

  /** Mesma métrica do Dashboard Z3: % ainda disponível do orçamento gastável. */
  const fillPct =
    totalSpendable > 0 && remaining != null
      ? Math.min(100, Math.max(0, (remaining / totalSpendable) * 100))
      : 0;

  const spent = Number(overview?.total_spent_eur ?? 0);
  const spentPctOfSpendable =
    totalSpendable > 0 ? Math.min(100, Math.max(0, (spent / totalSpendable) * 100)) : 0;

  const daysLeft = activeCycle ? cycleDaysRemaining(activeCycle) : null;

  const periodLabel =
    activeCycle?.start && activeCycle?.end
      ? formatSelfiePeriodLabel(activeCycle.start, activeCycle.end)
      : '';

  const isOverBudget = remaining != null && remaining < 0;
  const isTightBudget =
    remaining != null && (isOverBudget || fillPct <= 10 || spentPctOfSpendable >= 90);
  const displayRemaining = remaining != null ? Math.max(0, remaining) : null;

  const showBar = Boolean(overview?.has_income) && totalSpendable > 0 && remaining != null;

  /** `.pen` `--tight` › `note-pill`: só o excesso; o contexto de dias vai na escala. */
  const overBudgetNote = isOverBudget
    ? pt.expenses.hero.overBudgetLabel.replace('{amount}', formatEUR(Math.abs(remaining)))
    : null;

  const daysLine =
    daysLeft == null
      ? null
      : isTightBudget && !isOverBudget
        ? pt.expenses.hero.lowBudgetContext.replace('{days}', String(daysLeft))
        : daysLeft === 1
          ? pt.expenses.hero.dayLeft
          : pt.expenses.hero.daysLeft.replace('{days}', String(daysLeft));

  return (
    <CycleHeroShell
      variant={isTightBudget ? 'tight' : 'hero'}
      cycles={cycles}
      cycleIndex={cycleIndex}
      onCycleIndexChange={onCycleIndexChange}
      chipLabel={pt.expenses.hero.cycleChip.replace('{period}', periodLabel)}
      prevAriaLabel={pt.expenses.cycle.prev}
      nextAriaLabel={pt.expenses.cycle.next}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
      className={className}
      footer={
        <>
          {/* `.pen` › `foot` › `tiles`: duas colunas iguais, gap $space-4 */}
          <div className="flex min-w-0 gap-space-4">
            <div className="flex min-w-0 flex-1 flex-col gap-space-1">
              <p className="type-section text-sage-50/80">
                {pt.expenses.hero.savingsReserved}
              </p>
              <p className="type-display-stat text-sage-50">
                {savingsReserved != null ? formatEUR(savingsReserved) : '—'}
              </p>
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-space-1">
              <p className="type-section flex min-w-0 items-start gap-space-1 text-sage-50/80">
                <span className="min-w-0 flex-1">{pt.expenses.hero.savingsRate}</span>
                <HelpTooltip
                  appearance="plain"
                  label={pt.dashboard.cycleSummary.savingsRateTooltip}
                  content={pt.dashboard.cycleSummary.savingsRateTooltip}
                />
              </p>
              <p className="type-display-stat text-sage-50">
                {savingsPct != null ? `${savingsPct.toFixed(0)}%` : '—'}
              </p>
            </div>
          </div>

          {/* `.pen` › `foot` › `link-row` */}
          <div className="flex pt-space-4">
            <Link
              to={paths.budget}
              className="type-body-sm inline-flex items-center gap-space-1 border-b border-sage-50/20 pb-0.5 font-semibold text-sage-50"
            >
              {pt.expenses.monitor.planBudgetLink}
              <CaretRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </div>
        </>
      }
    >
      {/* `.pen` › `head` */}
      <div className="flex min-w-0 flex-col gap-space-2">
        <div className="flex min-w-0 items-center gap-space-2 text-sage-50/80">
          <span className="type-body-sm min-w-0 flex-1">
            {pt.expenses.hero.remainingLabel}
          </span>
          {isTightBudget ? (
            <Warning className="h-4 w-4 shrink-0" aria-hidden />
          ) : (
            <HelpTooltip
              appearance="plain"
              label={pt.common.cycleTooltip}
              content={pt.common.cycleTooltip}
            />
          )}
        </div>

        <p className="type-display-hero text-sage-50">
          {displayRemaining != null ? formatEUR(displayRemaining) : '—'}
        </p>

        {totalSpendable > 0 && remaining != null ? (
          <p className="type-body-sm text-sage-50/80">
            {pt.expenses.hero.ofBudget.replace('{amount}', formatEUR(totalSpendable))}
          </p>
        ) : null}
      </div>

      {/* `.pen` › `bar-block` */}
      {showBar ? (
        <div className="flex min-w-0 flex-col gap-space-2">
          <HeroProgressBar
            variant={isTightBudget ? 'warning' : 'filled'}
            percentage={fillPct}
            ariaLabel={pt.expenses.hero.remainingLabel}
          />
          <div className="flex items-center justify-between gap-space-2 text-sage-50/80">
            <span className="type-label-meta">
              {pt.expenses.hero.availablePct.replace('{pct}', String(Math.round(fillPct)))}
            </span>
            {daysLine ? <span className="type-label-meta text-right">{daysLine}</span> : null}
          </div>

          {overBudgetNote ? (
            <div className="flex pt-space-2">
              <span className="type-label inline-flex min-w-0 items-center gap-space-2 rounded-ds-full bg-warning-500 px-space-3 py-space-1 text-stone-900">
                <Warning className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {overBudgetNote}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
    </CycleHeroShell>
  );
}

export default ExpensesCycleHero;
